/**
 * Tenant switcher.
 *
 * Shown only when the person actually holds more than one membership — for the
 * overwhelming majority of users this is noise, so it stays hidden.
 *
 * Switching means new tokens (the tenant lives in the JWT claim), then a HARD cache
 * clear. Invalidation is not enough: stale rows from the previous tenant must never
 * be reachable, not even for one frame.
 */

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Check, ChevronDown } from 'lucide-react'
import { useAuth } from '@/app/providers/AuthProvider'
import { useToast } from '@/app/providers/ToastProvider'
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/Menu'
import { toUserMessage } from '@/services/api/errors'

/**
 * Memberships are known at login. They are not re-fetchable mid-session (the
 * backend exposes them only on the OTP verify response), so we keep what we learned.
 */
const MEMBERSHIPS_KEY = 'tf.memberships'

export interface StoredMembership {
  tenant_id: string
  tenant_name: string
  role: string
}

export function rememberMemberships(memberships: StoredMembership[]): void {
  try {
    sessionStorage.setItem(MEMBERSHIPS_KEY, JSON.stringify(memberships))
  } catch {
    /* switcher will simply stay hidden */
  }
}

export function readMemberships(): StoredMembership[] {
  try {
    const raw = sessionStorage.getItem(MEMBERSHIPS_KEY)
    return raw ? (JSON.parse(raw) as StoredMembership[]) : []
  } catch {
    return []
  }
}

export function TenantSwitcher() {
  const { tenant, switchTenant } = useAuth()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [switching, setSwitching] = useState(false)
  const memberships = readMemberships()

  if (memberships.length < 2) return null

  async function handleSwitch(tenantId: string) {
    if (tenantId === tenant?.id || switching) return
    setSwitching(true)
    try {
      const bundle = await switchTenant(tenantId)
      // Hard reset — see the note at the top of this file.
      queryClient.clear()
      toast.success(`Switched to ${bundle.tenant.name}`)
    } catch (error) {
      toast.error('Could not switch organisation', toUserMessage(error))
    } finally {
      setSwitching(false)
    }
  }

  return (
    <Menu
      trigger={
        <button
          type="button"
          className="flex max-w-[12rem] items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium text-fg-muted transition-colors hover:bg-surface-sunken hover:text-fg"
          aria-label="Switch organisation"
        >
          <Building2 className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{tenant?.name}</span>
          <ChevronDown className="size-3 shrink-0" aria-hidden />
        </button>
      }
    >
      <MenuLabel>Your organisations</MenuLabel>
      <MenuSeparator />
      {memberships.map((membership) => (
        <MenuItem
          key={membership.tenant_id}
          onSelect={() => void handleSwitch(membership.tenant_id)}
          disabled={switching}
          icon={
            membership.tenant_id === tenant?.id ? (
              <Check className="size-3.5 text-accent" aria-hidden />
            ) : (
              <span className="size-3.5" aria-hidden />
            )
          }
        >
          <span className="flex-1 truncate">{membership.tenant_name}</span>
          <span className="ml-2 text-2xs capitalize text-fg-subtle">{membership.role}</span>
        </MenuItem>
      ))}
    </Menu>
  )
}
